<template>
    <div class="el-picker-panel__content">
        <div class="el-date-picker__header">
            <AGlyph
                name="chevron-double-left--16"
                class="el-date-picker__header-btn"
                :aria-label="currentView === 'year' ? 'Previous Decade' : 'Previous Year'"
                @click="prevYear"
            />
            <AGlyph
                v-if="currentView === 'date'"
                name="i-chevron-left--16"
                class="el-date-picker__header-btn el-date-picker__header-btn--with-spacer-left"
                aria-label="Previous Month"
                @click="prevMonth"
            />
            <span class="el-date-picker__label-wrapper">
                <span
                    v-show="currentView === 'date'"
                    role="button"
                    class="el-date-picker__header-label el-date-picker__header-label-month"
                    :class="{ active: currentView === 'month' }"
                    :title="monthLabel"
                    @click="showMonthPicker"
                >
                    {{ monthLabel }}
                </span>
                <span
                    role="button"
                    class="el-date-picker__header-label el-date-picker__header-label-year"
                    :class="{
                        disabled: currentView === 'year',
                        'el-date-picker__header-label-year--full-width': currentView !== 'date',
                    }"
                    @click="showYearPicker"
                >
                    {{ yearLabel }}
                </span>
            </span>
            <AGlyph
                v-if="currentView === 'date'"
                name="i-chevron-right--16"
                class="el-date-picker__header-btn el-date-picker__header-btn--with-spacer-right"
                aria-label="Next Month"
                @click="nextMonth"
            />
            <AGlyph
                name="chevron-double-right--16"
                class="el-date-picker__header-btn"
                :aria-label="currentView === 'year' ? 'Next Decade' : 'Next Year'"
                @click="nextYear"
            />
        </div>

        <DateTable
            v-show="currentView === 'date'"
            :first-day-of-week="firstDayOfWeek"
            :panel-date="panelDate"
            :model-value="modelValue"
            @pick="emit('pick', $event)"
        />

        <MonthTable
            v-show="currentView === 'month'"
            :date="panelDate"
            :model-value="modelValue"
            @pick="onPickMonth"
        />

        <YearTable
            v-show="currentView === 'year'"
            :date="panelDate"
            :model-value="modelValue"
            @pick="onPickYear"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import DateTable from '@core/components/date-picker/basic/DateTable.vue';
import MonthTable from '@core/components/date-picker/basic/MonthTable.vue';
import YearTable from '@core/components/date-picker/basic/YearTable.vue';
import {
    prevMonth as _prevMonth,
    nextMonth as _nextMonth,
    prevYear as _prevYear,
    nextYear as _nextYear,
} from '@core/components/date-picker/util/index';
import AGlyph from '@core/components/glyph/glyph.vue';

const props = withDefaults(
    defineProps<{
    modelValue?: Date | null;
    firstDayOfWeek?: number;
  }>(),
    {
        modelValue: null,
        firstDayOfWeek: 1,
    },
);

const emit = defineEmits<{ (e: 'pick', d: Date): void }>();

const currentView = ref<'date' | 'month' | 'year'>('date');

const inner = ref<Date>(props.modelValue ? new Date(props.modelValue) : new Date());
watch(
    () => props.modelValue,
    (v) => {
        if (v) inner.value = new Date(v);
    },
);

const panelDate = computed(() => inner.value);
const monthLabel = computed(() =>
    panelDate.value.toLocaleString('en-US', { month: 'short' }),
);

function decadeStart(d: Date) {
    const y = d.getFullYear();
    return Math.floor(y / 10) * 10 - 1;
}
const yearLabel = computed(() => {
    if (currentView.value === 'year') {
        const s = decadeStart(panelDate.value);
        return `${s + 1} - ${s + 10}`;
    }
    return String(panelDate.value.getFullYear());
});

function prevMonth() {
    inner.value = _prevMonth(inner.value);
}
function nextMonth() {
    inner.value = _nextMonth(inner.value);
}
function prevYear() {
    inner.value = _prevYear(inner.value, currentView.value === 'year' ? 10 : 1);
}
function nextYear() {
    inner.value = _nextYear(inner.value, currentView.value === 'year' ? 10 : 1);
}

function showMonthPicker() {
    currentView.value = 'month';
}
function showYearPicker() {
    currentView.value = 'year';
}

function onPickMonth(d: Date) {
    inner.value = new Date(inner.value.getFullYear(), d.getMonth(), 1);
    currentView.value = 'date';
}
function onPickYear(d: Date) {
    inner.value = new Date(d.getFullYear(), inner.value.getMonth(), 1);
    currentView.value = 'month';
}
</script>
